<template>
  <PageView :loading="loading" :logo="logo" header-class="bg-light" class="bg-light d-flex flex-column flex-grow-1" container-class="d-flex flex-column flex-grow-1">
    <ScrollDownIndicatorComponent ref="scrollDownIndicator" />
    <div class="card d-flex flex-column flex-grow-1" v-if="participation">
      <div class="card-body pt-0 px-0 d-flex flex-column flex-grow-1">
        <TravelBannerComponent :participation="participation" />

        <div class="row m-2 m-md-3 mt-0 d-flex flex-column flex-grow-1">
          <h2 class="card-title text-center my-4 d-flex justify-content-between flex-column flex-md-row">
            <div class="col text-start d-none d-md-block">
              <router-link custom :to="reasonRequested ? { name: 'travel-slug', params: { slug: participation.travel.slug } } : { name: 'home' }" v-slot="{ navigate }">
                <button class="btn btn-outline-primary text-nowrap" @click="navigate">
                  <i class="fa-solid fa-angle-left me-2"></i> Retour
                </button>
              </router-link>
            </div>
            <div>Non-participation</div>
            <div class="col"></div>
            <div class="col text-start d-block d-md-none mt-2">
              <router-link custom :to="reasonRequested ? { name: 'travel-slug', params: { slug: participation.travel.slug } } : { name: 'home' }" v-slot="{ navigate }">
                <button class="btn btn-outline-primary text-nowrap" @click="navigate">
                  <i class="fa-solid fa-angle-left me-2"></i> Retour
                </button>
              </router-link>
            </div>
          </h2>

          <div v-if="!reasonRequested" class="d-flex flex-column mx-auto justify-content-center text-center">
            <p>
              <span v-if="participation.travel.refusalLongLabel">{{ participation.travel.refusalLongLabel }}</span>
              <span v-else>Vous avez décidé de passer votre tour pour le séjour <b>{{ participation.travel.name }}</b></span> <span v-if="participation.travel.isoCode" class="ms-2 fi" :class="['fi-' + participation.travel.isoCode.toLowerCase()]"></span>
            </p>
          </div>
          <div v-else class="d-flex flex-column col-12 col-md-10 col-lg-8 col-xl-6 mx-auto justify-content-center">
            <p>
              <span v-if="participation.travel.refusalLongLabel">{{ participation.travel.refusalLongLabel }}</span>
              <span v-else>Vous avez décidé de passer votre tour pour le séjour <b>{{ participation.travel.name }}</b></span> <span v-if="participation.travel.isoCode" class="ms-2 fi" :class="['fi-' + participation.travel.isoCode.toLowerCase()]"></span>
            </p>
            <div class="mb-3">
              <SelectInputComponent
                v-if="participation.travel.refusalReasons && participation.travel.refusalReasons.length > 0"
                value-key="invitation.refusalReason"
                label="Raison"
                placeholder="Sélectionnez une raison"
                :required="true"
                class="mb-3"
                :disabled="isSubmitting"
                :options="Object.values(participation.travel.refusalReasons).map(reason => ({ value: reason, label: reason }))"
              />
              <!-- OC Philipines -->
              <div class="pb-3" v-if="participation.travel.id == 15">
                Contacter Alexandre Turjman :<br><a href="mailto:alexandre.turjman@optical-center.com" target="_blank">alexandre.turjman@optical-center.com</a>
              </div>

              <SelectInputComponent
                v-if="participation.travel.refusalReasons2 && participation.travel.refusalReasons2.length > 0"
                value-key="invitation.refusalReason2"
                label="Vu avec"
                placeholder="Vu avec"
                :required="true"
                class="mb-3"
                :disabled="isSubmitting"
                :options="Object.values(participation.travel.refusalReasons2).map(reason => ({ value: reason, label: reason }))"
              />
            </div>
            <div class="d-flex justify-content-end gap-2">
              <!-- <button type="submit" class="btn btn-outline-primary align-self-end" :disabled="isSubmitting" @click="router.push({ name: 'home' })">
                Retour</button> -->
              <button type="submit" class="btn btn-primary align-self-end" :disabled="isSubmitting" @click="save">
                <span
                  v-if="isSubmitting"
                  class="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Valider
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PageView>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue';
import { getPictureUrl } from '@/compositions/helper';
import { useForm } from 'vee-validate';
import * as zod from 'zod';
import { useNotificationsStore } from '@/stores/notificationStore';
import { toTypedSchema } from '@vee-validate/zod';
import { useRouter } from 'vue-router';

import travelApi from '@/api/travelApi'
import PageView from '@/views/PageView.vue';

const router = useRouter();
const notificationsStore = useNotificationsStore();
const loading = ref(true);

const props = defineProps<{ id?: number; slug?: string }>()
const participation = ref<UserParticipation|null>(null);

const reasonRequested = computed(() => {
  return !(
         participation.value?.invitation?.refusalDate
      || participation.value?.travel?.needRefusalReason === false
      || !participation.value?.travel?.refusalReasons
      || participation.value?.travel?.refusalReasons.length === 0
  )
})

const schema = toTypedSchema(zod.object({
  invitation: zod.object({
    refusalReason: zod.string()
      .optional()
      .refine((val) => !participation.value?.travel.needRefusalReason || (val && val.length > 0), {message: "Veuillez sélectionner une raison" }),
    refusalReason2: zod.string()
      .optional()
      .refine((val) => !participation.value?.travel.refusalReasons2 || (val && val.length > 0), {message: "Veuillez sélectionner une raison" })
  }).optional()
}));

const {
  handleSubmit,
  isSubmitting,
  resetForm,
  values: formValues,
} = useForm<UserParticipation>({
  validationSchema: schema,
  keepValuesOnUnmount: true,
  validateOnMount: false,
});

const logo = computed(() => {
  if (participation.value?.travel.logo?.key) {
    return getPictureUrl(participation.value.travel.logo.key);
  }

  return undefined;
});

const save = async () => {
  return await handleSubmit(async () => {

    const saveRefusal = props.id
    ? travelApi.saveRefusal(props.id,formValues.invitation?.refusalReason)
    : props.slug
      ? travelApi.saveRefusalBySlug(props.slug,formValues.invitation?.refusalReason)
      : null

    if (!saveRefusal) {
      router.push({ name: 'home' })
      return
    }

    saveRefusal.then((response) => {
      notificationsStore.notifySuccess('Votre non-participation a bien été enregistrée.');
      resetForm({values: response.data}, { force: true });
      participation.value = response.data;
      return true;
    }).catch((error: Error) => {
      notificationsStore.notifyError('Erreur lors de l\'enregistrement',error.message);
      return false;
    });
  },(result) => {
      const errors = [...new Set(Object.keys(result.errors)
      .map((key:string) => {
        return result.errors[key as keyof typeof result.errors]
      }))];
      notificationsStore.notifyError(errors.slice(0, 5).join('<br>'));
      return false;
  })()
}

onMounted(() => {
  const loadParticipation = props.id
    ? travelApi.getParticipation(Number(props.id))
    : props.slug
      ? travelApi.getParticipationBySlug(props.slug)
      : null

  if (!loadParticipation) {
    router.push({ name: 'home' })
    return
  }

  loadParticipation.then((response) => {
    participation.value = response.data;

    if(participation.value.invitation?.refusalDate == null && participation.value.travel.needRefusalReason === false)
      save();
  }).catch((error: Error) => {
    notificationsStore.notifyError('Erreur lors du chargement de la page',error.message);
  }).finally(() => {
    loading.value = false;
  });
});
</script>
